import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import {
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
} from '@se/constraint';
import { IConfigColumnGridControl, IDataSource } from '@se/cp.common';
import { CultureService } from '@se/platform';
import { ButtonClick, Logger, ModalDialogNotificationService, NotificationType } from '@se/se.ui.html5';
import {
    Column,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    ColDef,
    GridSizeChangedEvent,
    IRowNode    
} from 'ag-grid-community';
import { last } from 'lodash-es';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { filter, first, tap, withLatestFrom, takeUntil } from 'rxjs/operators';

import { CpEntity, CpEntityList, CpGridControl, ICpEntity } from '../../cp-configuration/view-model';
import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';
import { cpSave } from '../../store/cp.actions';
import { AbstractControlComponent } from '../abstract-control';

@Component({
    selector: 'cp-grid',
    templateUrl: './grid.component.html',
    styleUrls: ['./grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpGridComponent extends AbstractControlComponent implements OnDestroy, OnInit {
    @Output() addRow = new EventEmitter<ICpEntity>();
    @Output() deleteRow = new EventEmitter<any>();

    columnDefs: ColDef[] = [];
    gridOptions: GridOptions = {};
    control!: CpGridControl;
    model: ICpEntity[] | undefined = undefined;
    groupHeight = 20;
    allowColumnToFit!: boolean;
    noRowsTemplate!: string;

    @ViewChild('tabRef', { read: ElementRef })
    tabElement!: ElementRef;

    @ViewChild('tabGuard')
    tabGuardElement!: ElementRef;

    tabGuardSub: any;
    tabGuardSwitch!: boolean;

    focusInObs!: Observable<FocusEvent>;
    tabObs!: Observable<KeyboardEvent>;

    // VALIDATION
    formGroup: FormGroup = new FormGroup({});
    // ------
    private gridApi!: GridApi;
    private refreshTable = false;
    private actionSubscription?: Subscription;
    private tabSub?: Subscription;

    constructor(
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        private actions$: Actions,
        private modelService: CpModelService,
        private datasourceService: CpDatasourceService,
        private cultureService: CultureService,
        private translate: TranslateService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private configuratorService: SeConstraintConfiguratorService,
        private constraintFilterService: SeConstraintApplierService,
        @Optional() logger?: Logger,
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    ngOnInit() {
        super.ngOnInit();
        this.tabGuardSwitch = this.control.isGridEditable && this.control.firstEditableColumnIndex !== null;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.actionSubscription?.unsubscribe();
        this.tabSub?.unsubscribe();
        this.gridApi?.removeEventListener('cellValueChanged', this.notifyModelChange);
    }

    onReportSave() {
        this.actionSubscription = this.actions$.pipe(ofType(cpSave)).subscribe(() => {
            this.refreshTable = true;
            this.gridApi.expireValueCache();
        });
    }

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.fitColumnIfNeed(this.gridApi);
        this.gridApi?.addEventListener('cellValueChanged', this.notifyModelChange);
        // ----- VALIDATION
        this.createFormControlsAndSubscribe();
        this.gridApi.refreshCells({ force: true });
        // ------

        this.noRowsTemplate = this.translate.instant('se-ui-datagrid_no_row_overlay');

        // Gestione TAB
        if (this.tabGuardSwitch) {
            this.tabObs = fromEvent<KeyboardEvent>(document, 'keydown').pipe(filter((ev) => ev.key === 'Tab'));
            this.focusInObs = fromEvent<FocusEvent>(this.tabGuardElement.nativeElement, 'focusin').pipe(
                tap(() => {
                    this.tabGuardElement.nativeElement.blur();
                }),
            );
            this.tabSub = this.focusInObs.pipe(withLatestFrom(this.tabObs)).subscribe(([_, keyboardEv]) => {
                if (!keyboardEv.shiftKey) {
                    this.preventTab();
                }
            });
        }

        // Gestione enable/disable-readonly a runtime
        this.updateFormControl();
    }

    notifyModelChange = (_: any) => {
        this.edited.emit();
    };

    protected updateFormControl() {
        // Gestione enable/disable-readonly a runtime
        if (this.gridApi) {
            const allColumns = this.gridApi.getColumns();
            if (allColumns && allColumns.length > 0) {
                this.gridApi.setColumnVisible(allColumns[0], !this.isDisabled); //ActionButtonColumn
            }

            this.gridOptions.suppressRowHoverHighlight = this.isDisabled;
            this.gridOptions.isRowSelectable = () => {
                return !this.isDisabled;
            };
            this.gridOptions.suppressRowClickSelection = this.isDisabled;

            // disable/enable gli editor su tutte le celle
            this.control.control.config[0].readonly = this.isDisabled;
            this.fitColumnIfNeed(this.gridApi);
        }
    }

    private createFormControlsAndSubscribe() {
        this.createFormControls();

        this.formGroup.statusChanges.subscribe((status) => {
            if (status === 'INVALID') {
                // provare un generico errore sulla griglia
                this.formControl?.setErrors([{ required: true }]);
            } else {
                this.formControl?.setErrors(null);
            }
        });
    }

    private createFormControls() {
        this.handleFormControls(FormControlAction.ADD);
        this.gridApi.refreshCells({ force: true });
    }

    private handleFormControls(action: string = FormControlAction.ADD) {
        if (!this.gridApi) {
            return;
        }
        let columns = this.gridApi.getColumns();
        if (FormControlAction.ADD === action) {
            this.gridApi.forEachNode((rowNode: IRowNode) => {
                columns
                    ?.filter((column) => !!column.getColDef().field)
                    .forEach((column: Column) => {
                        const key = this.createKey(rowNode, column);
                        if (!this.formGroup.controls[key]) {
                            // elenco dei validatori da associare a ogni cella sono presi da colDef.validators
                            // questo campo è popolato in cp-grid-controls
                            const property = last(column.getColDef().field?.split('.')) as string;
                            const colDef: any = column.getColDef();
                            const entityData: CpEntity = rowNode.data;
                            if ('validators' in colDef) {
                                this.formGroup.addControl(
                                    key,
                                    new FormControl(entityData.getPropertyPrimitiveValue(property), {
                                        validators: colDef.validators,
                                    }),
                                );
                            }
                        }
                    });
            });
        } else {
            Object.keys(this.formGroup.controls).forEach((key) => {
                this.formGroup.removeControl(key);
            });
        }
    }

    getContext() {
        return {
            formGroup: this.formGroup,
            createKey: this.createKey,
        };
    }

    private createKey(rowNode: IRowNode, column: Column): string {
        const data = rowNode.data.properties;
        return `${data.guid || data.Id}${column.getColId()}`;
    }

    private preventTab(): void {
        const numRows = this.model?.length ?? 0;
        if (numRows > 0) {
            this.editOrFocusTheLeftmostCell('edit');
        } else {
            const firstBtn = this.tabElement.nativeElement.getElementsByTagName('se-button-cell-renderer')[0];
            if (firstBtn && this.control.firstEditableColumnIndex) {
                firstBtn.firstChild.focus();
            }
        }
    }

    private editOrFocusTheLeftmostCell(editOrFocus: 'edit' | 'focus'): void {
        if (this.control.firstEditableColumnIndex) {
            const firstCol =
                this.gridApi?.getAllDisplayedColumns()[this.control.firstEditableColumnIndex];
            if (firstCol) {
                if (editOrFocus === 'edit') {
                    this.gridApi?.startEditingCell({ rowIndex: 0, colKey: firstCol.getColId() });
                } else {
                    this.gridApi?.setFocusedCell(0, firstCol);
                }
                this.gridApi?.ensureIndexVisible(0);
                this.gridApi?.ensureColumnVisible(firstCol);
            }
        }
    }

    _onCellEditingStopped(_: any) {
        this._onColumnResized();
    }

    _onColumnResized() {
        this.gridApi.resetRowHeights();
    }

    _onGridSizeChanged(params: GridSizeChangedEvent) {
        this.fitColumnIfNeed(params.api);
        this.changeRowHeight(window.innerWidth);
    }

    _onChangeFromAdContent() {
        this.edited.emit();
    }

    private changeRowHeight(screenWidth: number): void {
        if (screenWidth > 1281 && screenWidth < 1440 && this.gridOptions.rowHeight !== 35) {
            this.gridOptions.rowHeight = 35;
            this.gridApi.resetRowHeights();
        } else if (screenWidth > 801 && screenWidth < 1280 && this.gridOptions.rowHeight !== 30) {
            this.gridOptions.rowHeight = 30;
            this.gridApi.resetRowHeights();
        } else if ((screenWidth > 1441 || screenWidth <= 801) && this.gridOptions.rowHeight !== 40) {
            this.gridOptions.rowHeight = 40;
            this.gridApi.resetRowHeights();
        }
    }

    private fitColumnIfNeed(gridApi: GridApi) {
        gridApi.sizeColumnsToFit();
    }

    // shorcut 'add new row'
    _onAltEnter(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.addNewRow();
    }

    private addNewRow() {
        const newEntity = this.modelService.createModelFromPlaceholder(
            this.control.dataBinding.entity,
            this.control.dataBinding.entity.toStringSimple(),
        );
        this.edited.emit();
        this.addRow.emit(newEntity);
        this.setNoDataOverlay();
        setTimeout(() => {
            const columnState = this.gridApi?.getColumnState() ?? [];
            const prevState = [...columnState];
            prevState.forEach((state) => (state.sort = null));
            this.gridApi?.applyColumnState({ state: prevState, applyOrder: true });
            this.startEditing(this.control.columnsConfigs);
        }, 0);
    }

    setDataSource = (idDataSource: string, useObject: boolean = true): IDataSource | Array<string> => {
        let currentDataSource: IDataSource | Array<string> = [];

        if (idDataSource) {
            currentDataSource = this.datasourceService.getById(idDataSource) ?? [];
        }

        if (currentDataSource && useObject === false) {
            currentDataSource = this.datasourceService.getById(idDataSource)?.map((item) => item.Description) ?? [];
        }

        return currentDataSource;
    };

    protected init() {
        this.control.setModelService(this.modelService);

        this.dataBind();
        this.setCultureServiceInfo();

        this.control.removeRow = this.removeRow;
        this.control.addRow = this.onAddRow;
        this.control.setDataSource = this.setDataSource;

        this.columnDefs = this.control.columnDefs;
        this.gridOptions = this.control.gridOptions;

        this.gridOptions.rowHeight = 50;

        // Commertare per debuggare gli editor
        this.gridOptions.stopEditingWhenCellsLoseFocus = true;

        this.allowColumnToFit = this.control.allowColumnToFit;
        /**passo le istanze dei servizi al controllo (modello) */
        this.control.constraintFilterService = this.constraintFilterService;
        this.control.configuratorService = this.configuratorService;
        this.control.constraintNotifier = this.constraintNotifier;
    }

    private dataBind() {
        if (!this.control.dataBinding) {
            return;
        }

        this.modelService
            .registerList(this.control.dataBinding.entity)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((model) => {
                this.myModelChange(model);
            });

        this.formControl = new FormControl(this.model, [
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, false),
        ]);
    }

    private setCultureServiceInfo() {
        this.control.maskedDateFormat = this.cultureService.getShortDatePattern() || null;
        this.control.maskedTimeFormat = this.cultureService.getShortTimePattern() || null;
        this.control.maskedDateTimeFormat = this.cultureService.getShortDateTimePattern() || null;
        this.control.maskedDateSeparator = this.cultureService.getDateSeparator() || null;
        this.control.maskedTimeSeparator = this.cultureService.getTimeSeparator() || null;
        this.control.separatorInUse = this.cultureService.getDecimalSeparator() || ',';
    }

    protected myModelChange(model: CpEntityList | undefined) {
        if (!this.model) {
            //SONIA
            this.model = model ? model.activeEntities : [];
        } else {
            if (model) {
                const modelChangeType: ModelChangeType = this.identifyModelChangeType(model);

                switch (modelChangeType) {
                    case ModelChangeType.ADD_ROW:
                        this.onModelChangeAddRow(model);
                        break;
                    case ModelChangeType.CHANGE_MODEL:
                    case ModelChangeType.REMOVE_ROW:
                        this.onModelChangeRemoveRow(model);
                        break;
                    case ModelChangeType.NONE:
                        this.onModelChangeNone();
                        break;
                    default:
                        break;
                }
            }
        }
        return false;
    }

    protected onModelChange(_: CpEntityList | undefined) {
        return false;
    }

    private onModelChangeAddRow(model: CpEntityList) {
        if (!this.gridApi) {
            return;
        }

        // SYNC AG GRID with Model
        const updateResult = this.gridApi.applyTransaction({
            add: [model.activeEntities[model.activeEntities.length - 1]],
        });

        // VALIDATION
        const newNode = updateResult?.add[0];
        if (!newNode) {
            return;
        }

        this.gridApi
            .getColumns()
            ?.filter((column) => !!column.getColDef().field)
            .forEach((column: Column) => {
                const key = this.createKey(newNode, column);
                if (this.formGroup.controls[key]) {
                    return;
                }
                const colDef: any = column.getColDef();
                const property = last(colDef.field?.split('.')) as string;
                const entity: CpEntity = newNode.data;
                this.formGroup.addControl(
                    key,
                    new FormControl(entity.getPropertyPrimitiveValue(property), {
                        validators: 'validators' in colDef ? colDef.validators : [],
                    }),
                );
            });
        // ------
        this.model = model ? model.activeEntities : [];
    }

    private onModelChangeRemoveRow(model: CpEntityList) {
        if (this.gridApi) {
            let editingCells = this.gridApi.getEditingCells();
            this.handleFormControls(FormControlAction.REMOVE);
            this.gridApi.updateGridOptions({ rowData: model.activeEntities });
            this.gridApi.expireValueCache();
            this.gridApi.refreshCells({ force: true });
            this.model = model ? model.activeEntities : [];
            this.handleFormControls(FormControlAction.ADD);

            setTimeout(() => {
                if (!!editingCells && editingCells.length > 0) {
                    this.gridApi.startEditingCell({
                        rowIndex: editingCells[0].rowIndex,
                        colKey: editingCells[0].column,
                    });
                }
            });
        }
    }

    private onModelChangeNone() {
        if (this.gridApi) {
            let editingCells = this.gridApi.getEditingCells();

            if (!!editingCells && editingCells.length > 0) {
                this.gridApi.startEditingCell({
                    rowIndex: editingCells[0].rowIndex,
                    colKey: editingCells[0].column,
                });
            } else {
                this.gridApi.refreshCells({ force: true });
            }
        }
    }

    private getModelChangeType(model: CpEntityList): ModelChangeType {
        let output: ModelChangeType;

        if (JSON.stringify(this.model) === JSON.stringify(model.activeEntities)) {
            output = ModelChangeType.NONE;
        } else {
            output = ModelChangeType.CHANGE_MODEL;
        }

        return output;
    }

    private identifyModelChangeType(model: CpEntityList): ModelChangeType {
        // Extract key CpEntity from new model
        let newModelEntityIds: Array<string> = [];

        // Extract key CpEntity from actual model
        let currentModelEntityIds: Array<string> = [];

        if (model) {
            currentModelEntityIds = this.model?.map((entity) => entity.Id) ?? [];

            if (model.activeEntities) {
                newModelEntityIds = model.activeEntities.map((entity) => entity.Id);
            }
        }

        // Same number of elements
        if (newModelEntityIds.length === currentModelEntityIds.length) {
            const noNewEntity = this.newEntityPresent(currentModelEntityIds, newModelEntityIds);
            const needRefresh = this.refreshAllRow();
            return noNewEntity && !needRefresh ? ModelChangeType.NONE : ModelChangeType.CHANGE_MODEL;
        }

        // New Model with more elements
        if (newModelEntityIds.length > currentModelEntityIds.length) {
            let currentEntityModelLessOne = currentModelEntityIds.slice(0, newModelEntityIds.length);
            const allEntityArePresent = this.newEntityPresent(currentEntityModelLessOne, newModelEntityIds);
            if (newModelEntityIds.length - currentModelEntityIds.length === 1 && allEntityArePresent) {
                return ModelChangeType.ADD_ROW;
            }
            return ModelChangeType.CHANGE_MODEL;
        }

        // New Model with less elements
        if (newModelEntityIds.length < currentModelEntityIds.length) {
            const noNewEntity = this.newEntityPresent(newModelEntityIds, currentModelEntityIds);
            return noNewEntity ? ModelChangeType.REMOVE_ROW : ModelChangeType.CHANGE_MODEL;
        }

        return ModelChangeType.NONE;
    }

    private refreshAllRow() {
        if (this.refreshTable) {
            this.refreshTable = false;
            return true;
        }
        return false;
    }

    private newEntityPresent(ids1: Array<string>, ids2: Array<string>): boolean {
        return !!ids1.reduce((accumulator, currentValue) => accumulator && ids2.includes(currentValue), true);
    }

    onAddRow = () => {
        this.addNewRow();
    };

    removeRow = (cellRendererParams: ICellRendererParams, _: string) => {
        this.gridApi.stopEditing(true);
        this.modalDialogNotificationService
            .notify({
                message: 'Si è scelto di rimuovere una riga. Procedere?',
                title: this.translate.instant('Warning'),
                type: NotificationType.information,
                buttonYes: true,
                buttonNo: true,
                buttonOk: false,
                buttonCancel: false,
            })
            .pipe(first())
            .subscribe((button) => {
                if (button === ButtonClick.yes) {
                    this.modelService.removeEntity(cellRendererParams.data);

                    // Determino se mostrare o meno il no data overlay
                    this.setNoDataOverlay();
                    this.edited.emit();
                    this.deleteRow.emit();
                }
            });
    };

    refreshGrid() {
        this.gridApi.refreshCells({ force: true });
    }

    protected setNoDataOverlay(): void {
        if (this.gridApi.getModel().isEmpty()) {
            this.gridApi.showNoRowsOverlay();
        } else {
            this.gridApi.hideOverlay(); // nascondo l'overlay "no row to show" se ci sono righe... di qualunque tipo
        }
    }

    protected startEditing(columnConfigArray: Array<IConfigColumnGridControl>): void {
        let firstRequiredColumn = this.getFirstRequiredColumn(columnConfigArray);
        // apro l'editor se c'è almeno una colonna "REQUIRED"
        if (firstRequiredColumn) {
            this.gridApi.startEditingCell({
                rowIndex: (this.model?.length ?? 0) - 1,
                colKey: firstRequiredColumn,
            });
        }
    }

    protected getFirstRequiredColumn(columnConfigArray: any): any {
        const firstRequiredColumn = columnConfigArray.find((elem: IConfigColumnGridControl) => elem.required === true);
        return firstRequiredColumn?.dataBinding || null;
    }    
}

enum FormControlAction {
    ADD = 'ADD',
    REMOVE = 'REMOVE',
}

enum ModelChangeType {
    ADD_ROW = 'ADD_ROW',
    REMOVE_ROW = 'REMOVE_ROW',
    CHANGE_MODEL = 'CHANGE_MODEL',
    NONE = 'NONE',
}
